import {useSelector} from 'react-redux';
import {useVersioned} from 'coreUtils/adapters/versioned';

import {PREFERRED_COUNTRY} from 'coreClasses/cookies/config';
import CookiesMgr from 'coreClasses/cookies/CookiesMgr';
import CountryModel from 'coreClasses/countries/CountryModel';
import {EEventType} from 'coreClasses/events/IEvents';
import LocaleModel from 'coreClasses/locales/LocaleModel';
import LocalesMgr from 'coreClasses/locales/LocalesMgr';
import {useHistory, useRequestClass} from 'coreHooks';
import {
	receiveSiteChangingStatus,
	selectCountries,
	selectCurrentCountryCode,
	selectCurrentLanguageCode,
	selectLanguages,
} from 'coreStore/data/configurations/index';
import {RootState} from 'coreStore/types';
import {getHostnameByCountryCode, safeDecodeURI} from 'coreUtils/url';
import {getConfig} from 'coreUtils/utils';
import {setRootElementLanguageAttributes} from 'coreUtils/utils';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

import useSetUI from './useSetUI';

interface IExtendedLocalesMgr extends LocalesMgr {
	updateLocale: (country: CountryModel | string, language: string) => Promise<void>;
	updateCountry: (country: CountryModel | string) => void;
	updateLanguage: (language: string) => void;
	setCookiesProcessed: (newValue: boolean) => void;
	isCookiesProcessed: () => boolean;
	updateUrl: (url?: string | null, preserveCurrentParameters?: boolean, replace?: boolean) => void;
	getLocaleFromCookies: () => LocaleModel;
}

const useLocalesMgr = () => {
	const {managerRepository, isLocaleCookiesProcessed, setLocaleCookiesProcessed} = useGeneralProvider();
	const {isRemote, instanceKey, isCORSRequest} = useRequestClass();
	const setUI = useSetUI();

	const countries = useSelector((state: RootState) => selectCountries(state));
	const languages = useSelector((state: RootState) => selectLanguages(state));
	const countryCode = useSelector((state: RootState) => selectCurrentCountryCode(state));
	const languageCode = useSelector((state: RootState) => selectCurrentLanguageCode(state));

	const history = useHistory();
	const extendedLocalesMgr = managerRepository.getLocalesMgr() as IExtendedLocalesMgr;

	/**
	 * Updates the current url with a new locale code
	 * @param {CountryModel|string} country - country code // TODO: accept only one type
	 * @param {string} language - language code
	 * @returns {string} - updated url
	 */
	extendedLocalesMgr.updateLocale = async (country, language) => {
		const oldCountryModel = managerRepository.getLocalesMgr().getCurrentCountry();
		const newCountryCode = typeof country === 'string'
			? country
			: country.getCountryCode();
		const newCountryModel = managerRepository.getCountriesMgr().getCountryByCode(newCountryCode);
		const externalUrl = newCountryModel?.getExternalUrl();

		// @TODO: Remove clearCookie and set preferredCountry cookie before redirect instead.
		// This way, if a user selects an "external country", on their next visit
		// they get redirected automatically. This flow is a temporary WF requirement
		if (externalUrl) {
			CookiesMgr.removeCookie(PREFERRED_COUNTRY.cookieName);
			window.location.replace(externalUrl);

			return;
		}

		const currentSiteId = oldCountryModel?.getSiteId();
		const newSiteId = newCountryModel?.getSiteId();
		const promises: Promise<unknown>[] = [];

		managerRepository.getSpinnerMgr().show(true);

		if (newCountryCode !== oldCountryModel?.getCountryCode()) {
			managerRepository.getCustomerMgr().invalidateContext();

			promises.push(extendedLocalesMgr.setCountry(country));
		}

		promises.push(extendedLocalesMgr.setLanguage(language));

		await Promise.all(promises);

		extendedLocalesMgr.updateCookiesWithCurrentValues();
		extendedLocalesMgr.updateReduxStore();

		if (isRemote) {
			const redirectHostname = getHostnameByCountryCode(newCountryCode, newSiteId, instanceKey);
			const appConfig = getConfig();

			if (redirectHostname && redirectHostname !== appConfig.app.instanceHostname) {
				const redirectUrl = `${appConfig.app.instanceProtocol}://${redirectHostname}?country=${newCountryCode}&language=${language}`;

				window.location.href = redirectUrl;

				return;
			}
		}

		const {code, direction} = managerRepository.getLanguagesMgr().getLanguageAttributes(language);

		setRootElementLanguageAttributes(code, direction);

		managerRepository.getEventsMgr().emit(EEventType.CUSTOMER_CHANGED, {
			customer: managerRepository.getCustomerMgr().getCurrentCustomer(),
		});

		// set site changed
		if (currentSiteId !== newSiteId) {
			managerRepository.getStoreMgr().getStore().dispatch(receiveSiteChangingStatus('changed'));
		}

		// URL navigation (via useLocaleChangeHandler) will re-show the spinner through
		// StrictLocaleLoaderRouter; hide here when no navigation follows.
		managerRepository.getSpinnerMgr().show(false);

		return Promise.resolve();
	};

	/**
	 * Updates current country
	 */
	extendedLocalesMgr.updateCountry = (country) => {
		let language = extendedLocalesMgr.getCurrentLanguage().getLanguageCode();
		let countryModel = typeof country === 'string' ? managerRepository.getCountriesMgr().getCountryByCode(country) : country;

		if (!managerRepository.getLanguagesMgr().isLocaleCodeSupported(language, countryModel)) {
			language = countryModel.getDefaultLanguage();
		}

		extendedLocalesMgr.updateLocale(country, language);
	};

	/**
	 * Updates current language
	 */
	extendedLocalesMgr.updateLanguage = (language) => {
		extendedLocalesMgr.updateLocale(extendedLocalesMgr.getCurrentCountry(), language);

		const {code, direction} = managerRepository.getLanguagesMgr().getLanguageAttributes(language);

		setRootElementLanguageAttributes(code, direction);
	};

	/**
	 * Updates current url
	 * @param url {String|null} - new URL as string
	 * @param preserveCurrentParameters - If true, parameters from current location will be appended to the new URL
	 * @param replace {Boolean} - If true, uses `history.replace()` to update the URL without adding a new entry. If false, uses `history.push()` to add one.
	 */
	extendedLocalesMgr.updateUrl = (url = null, preserveCurrentParameters = true, replace = true) => {
		const {pathname, search, hash} = window.location;
		const newUrl = url && preserveCurrentParameters ? `${url}${search}${hash}` : url;
		const decodedNewUrl = newUrl ? safeDecodeURI(newUrl) : null;
		const targetUrl = safeDecodeURI(preserveCurrentParameters ? `${pathname}${search}${hash}` : pathname);
		const urlWithLocale = extendedLocalesMgr.updateLinkWithLocale(decodedNewUrl || targetUrl);

		if (safeDecodeURI(urlWithLocale) === targetUrl) {
			return;
		}

		if (!isLocaleCookiesProcessed) {
			return;
		}

		if (isCORSRequest) {
			return;
		}

		if (replace) {
			history.replace(urlWithLocale);
		} else {
			history.push(urlWithLocale);
		}

		setUI.setNavigatingToCorrectLocale(true);
	};

	/**
	 * getLocaleFromCookies
	 * @returns extendedLocalesMgr {LocaleModel} - Local model
	 */
	extendedLocalesMgr.getLocaleFromCookies = () => {
		try {
			return extendedLocalesMgr.parseLocaleFromCookies();
		} catch (e) {
			let fallbackLocale = null;

			try {
				fallbackLocale = extendedLocalesMgr.parseLocaleFromURL(
					window.location.hostname,
					window.location.pathname,
				);
			} catch (e) {
				fallbackLocale = extendedLocalesMgr.getDefaultLocale();
			}

			extendedLocalesMgr.updateLocale(fallbackLocale.getCountryCode(), fallbackLocale.getLanguageCode());

			return fallbackLocale;
		}
	};

	/**
	 * Checks if cookies processed
	 * @returns {boolean} true if cookies processed
	 */
	extendedLocalesMgr.isCookiesProcessed = () => {
		return isLocaleCookiesProcessed;
	};

	/**
	 * Set cookies processed flag
	 */
	extendedLocalesMgr.setCookiesProcessed = () => {
		setLocaleCookiesProcessed(true);
	};

	// The methods above read these values too.
	return useVersioned(
		extendedLocalesMgr,
		countries,
		languages,
		countryCode,
		languageCode,
		isLocaleCookiesProcessed,
		isRemote,
		instanceKey,
		isCORSRequest,
	);
};

export default useLocalesMgr;
